import clsx from "clsx";
import React, { CSSProperties, Fragment, useState } from "react";
import FlexBox from "../../../Components/FlexBox/FlexBox";
import Divider from "../../../Components/Divider/Divider";
import Icon from "../../../Foundation/Icon/Icon";
import Spinner from "../../../Foundation/Spinner/Spinner";
import Typography from "../../../Foundation/Typography/Typography";
import { useTheme } from "../../../Themes/defaultTheme";
import ProductLine, {
  EProductLineVariant,
} from "../../ProductLine/ProductLine";
import { IOrderAccordion } from "../OrderAccordion";
import {
  _OrderAccordionItemAction,
  _OrderAccordionItemHeader,
  _OrderAccordionItemImageWrapper,
  _OrderAccordionItemPanel,
  _OrderAccordionItemRoot,
} from "./OrderAccordionItem.styled";
import useBreakpoint from "../../../Components/Grid/hooks/useBreakpoint";

interface IOrderAccordionItemProps {
  active?: boolean;
  order: IOrderAccordion;
  onReorder?: (order: IOrderAccordion) => Promise<void> | void;
  reorderLabel?: React.ReactNode;
  deleteLabel?: string;
}

const imageStyle: CSSProperties = {
  position: "absolute",
  left: 0,
  right: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
};
const imageSize = 88;
const spacing = 24;

const OrderAccordionItem = (props: IOrderAccordionItemProps) => {
  const {
    order,
    active = false,
    onReorder,
    reorderLabel = "Recommander",
  } = props;
  const {
    products,
    formattedDate,
    shortFormattedDate,
    totalPrice,
    onClick,
    formattedQuantity,
  } = order;
  const leadProduct = (products && products[0]) || {};
  const moreProductsCount = products.length - 1;
  const { color } = useTheme();
  const [isActive, setIsActive] = useState<boolean>(active);
  const [loading, setLoading] = useState<boolean>(false);
  const { isMobile } = useBreakpoint();

  // TODO implement formatPrice with React Context
  const formatPrice = (price: number | string) => {
    return typeof price === "number" ? `${price.toFixed(2)} €` : price;
  };

  const handleHeaderClick = () => {
    setIsActive((v) => !v);
  };

  const handleReorderClick = async () => {
    setLoading(true);
    if (onClick) {
      await onClick();
    } else {
      onReorder && (await onReorder(order));
    }
    setLoading(false);
  };

  return (
    <_OrderAccordionItemRoot className={clsx({ active: isActive })}>
      <_OrderAccordionItemHeader>
        <FlexBox
          flex={1}
          style={{
            padding: isActive
              ? `16px ${spacing}px 0`
              : `${spacing}px ${spacing}px 0`,
            transition: "padding 0.6s ease-in-out",
          }}
        >
          <FlexBox flex={1} align="flex-start" gap={16}>
            <_OrderAccordionItemImageWrapper $size={imageSize}>
              <img src={leadProduct.image} style={imageStyle} />
            </_OrderAccordionItemImageWrapper>
            <FlexBox
              flex={1}
              direction="column"
              style={{
                padding: "0 0 36px",
              }}
            >
              <FlexBox flex={1} gap={16}>
                <Typography
                  style={{ flex: 1 }}
                  variant="h7"
                  color="placeholder"
                >
                  {leadProduct.name}
                </Typography>
                <Typography variant="h7" color="placeholder">
                  {formatPrice(totalPrice)}
                </Typography>
              </FlexBox>
              {isActive && (
                <Typography
                  style={{ flex: 1 }}
                  variant="body2"
                  color="greyText"
                >
                  {formatPrice(leadProduct.price)}
                </Typography>
              )}
              {!isActive && moreProductsCount > 0 && (
                <div onClick={handleHeaderClick}>
                  <Typography
                    style={{ flex: 1 }}
                    variant="body2"
                    color="greyText"
                  >
                    {formattedQuantity ||
                      (moreProductsCount > 0
                        ? `+${moreProductsCount} products`
                        : "")}
                  </Typography>
                </div>
              )}
            </FlexBox>
          </FlexBox>
        </FlexBox>
        <_OrderAccordionItemPanel className={clsx({ active: isActive })}>
          <div style={{ padding: "0px" }}>
            <Divider direction="horizontal" weight={1} />
            {products.map((item) => {
              if (item.id === leadProduct.id) return null;
              return (
                <Fragment key={item.id}>
                  <ProductLine
                    variant={EProductLineVariant.ORDER}
                    style={{ padding: `8px ${spacing}px 8px` }}
                    size={imageSize}
                    image={item.image}
                    name={item.name}
                    // price={item.price.toFixed(2)}
                    subtitle={formatPrice(item.price)}
                  />
                  <Divider direction="horizontal" weight={1} />
                </Fragment>
              );
            })}
          </div>
        </_OrderAccordionItemPanel>
        <FlexBox
          flex={1}
          justify="space-between"
          style={{
            padding: isActive
              ? `16px ${spacing}px ${spacing}px`
              : `0 ${spacing}px ${spacing}px`,
            marginTop: isActive ? 0 : -spacing,
          }}
        >
          <FlexBox
            align="center"
            justify="center"
            style={{ width: imageSize }}
            {...(isActive && { onClick: handleHeaderClick })}
          >
            {isActive && <Icon icon="arrowhead_up" color={color.greyText} />}
          </FlexBox>
          <_OrderAccordionItemAction style={{ margin: "0 0 0 12px" }}>
            <Icon icon="calendar" color={color.greyText} />
            <Typography variant="body2" color="greyText">
              {isMobile ? shortFormattedDate : formattedDate}
            </Typography>
          </_OrderAccordionItemAction>
          <_OrderAccordionItemAction
            onClick={handleReorderClick}
            style={{ margin: "0 0 0 auto" }}
          >
            {loading ? (
              <Spinner weight={0.1} />
            ) : (
              <Icon icon="reorder" color={color.champagne2} />
            )}
            <Typography variant="body2" color="champagne2">
              {reorderLabel}
            </Typography>
          </_OrderAccordionItemAction>
        </FlexBox>
      </_OrderAccordionItemHeader>
    </_OrderAccordionItemRoot>
  );
};

export default OrderAccordionItem;
